import { IFormSelectOption } from '../../../../../interfaces/forms/forms';
import { IStepExportFormInfo } from '../stepExport.schema';

// HS code for the hardcoded documents line, used whenever a shipment always ships documents
// (international letters, and USA/PR shipments with package type 'D').
export const DOCUMENTS_HS_CODE = '481720';

// The single hardcoded customs line for documents-only shipments.
export const documentsContentLine: IStepExportFormInfo['contents'][number] = {
	amountInPackage: 1,
	unitPrice: '0',
	price: '0',
	weight: '',
	contents: 'Documents',
	hsTariffCode: DOCUMENTS_HS_CODE,
	hsChapterTitle: 'PAPER AND PAPERBOARD; ARTICLES OF PAPER PULP, OF PAPER OR OF PAPERBOARD',
	hsHeadingTitle: 'Documents',
	countryOfOrigin: { value: 'IS', label: 'Ísland' },
	hsManualEntry: false,
};

// Weight per item applies to USA/PR gifts, or while no package type is chosen yet (not international letters)
export const isItemWeightRequired = (
	countryCode?: string,
	packageTypeValue?: string,
	isInternationalLetter?: boolean
): boolean =>
	(countryCode === 'US' || countryCode === 'PR') &&
	(!packageTypeValue || packageTypeValue === 'G') &&
	!isInternationalLetter;

// Computes a customs line's total value (Verðmæti) = unit price × quantity.
// Prices use Icelandic formatting where ',' is the decimal separator, so we normalise it to '.'
// for the maths and format the result back with a comma. Returns '' when inputs are incomplete.
export const computeLineValue = (unitPrice: string, quantity: '' | number): string => {
	const unit = parseFloat(String(unitPrice ?? '').replace(',', '.'));
	const qty = Number(quantity);
	if (isNaN(unit) || isNaN(qty) || quantity === '' || String(unitPrice ?? '') === '') return '';
	const total = unit * qty;
	// Keep up to 2 decimals, drop trailing zeros, and use a comma decimal separator to match input.
	return parseFloat(total.toFixed(2)).toString().replace('.', ',');
};

export const currencySelectOptions = (isUSAorPR?: boolean): IFormSelectOption[] =>
	isUSAorPR
		? [{ value: 'USD', label: 'USD' }]
		: [
				{ value: 'ISK', label: 'ISK' },
				{ value: 'USD', label: 'USD' },
				{ value: 'GBP', label: 'GBP' },
				{ value: 'EUR', label: 'EUR' },
				{ value: 'DKK', label: 'DKK' },
				{ value: 'NOK', label: 'NOK' },
				{ value: 'SEK', label: 'SEK' },
				{ value: 'CHF', label: 'CHF' },
				{ value: 'JPY', label: 'JPY' },
				{ value: 'CAD', label: 'CAD' },
				{ value: 'XDR', label: 'XDR' },
				{ value: 'PLN', label: 'PLN' },
		  ];

export const getPackageTypeSelectOptions = (
	t: any,
	isUSAorPR?: boolean,
	// When the recipient country is a PDDP country the shipment counts as a
	// company shipment under the new customs rules, so "Söluvara" (sale of
	// goods) must not be offered as a customs purpose.
	requiresBusinessAccountForCompanySender?: boolean
): IFormSelectOption[] =>
	isUSAorPR
		? [
				{ value: 'G', label: t('step-export.gift') },
				{ value: 'D', label: t('step-export.documents') },
		  ]
		: [
				{ value: 'G', label: t('step-export.gift') },
				{ value: 'S', label: t('step-export.commercial-sample') },
				{ value: 'R', label: t('step-export.returnGoods') },
				...(requiresBusinessAccountForCompanySender
					? []
					: [{ value: 'O', label: t('step-export.product') }]),
				{ value: 'A', label: t('step-export.other') },
		  ];

export const getReturnPolicies = (t: any): IFormSelectOption[] => [
	{ value: ShipmentReturnPolicy.ReturnToSender, label: t('step-export.return') },
	{ value: ShipmentReturnPolicy.Destroy, label: t('step-export.destroy') },
];

export enum ShipmentReturnPolicy {
	ReturnToSender = 'ReturnToSender',
	Destroy = 'Destroy',
}
